'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';
import type { Device } from './DeviceInventoryContent';

const statusConfig: Record<string, { label: string; dot: string; text: string }> = {
  online: { label: 'Online', dot: 'bg-green-400', text: 'text-green-400' },
  offline: { label: 'Offline', dot: 'bg-red-400', text: 'text-red-400' },
  latenza_alta: { label: 'Latenza Alta', dot: 'bg-amber-400', text: 'text-amber-400' },
  intermittente: { label: 'Intermittente', dot: 'bg-orange-400', text: 'text-orange-400' },
  manutenzione: { label: 'Manutenzione', dot: 'bg-purple-400', text: 'text-purple-400' },
};

const categoriaIcons: Record<string, string> = {
  laboratorio: 'BeakerIcon',
  ufficio: 'ComputerDesktopIcon',
  medico: 'HeartIcon',
  iot: 'CpuChipIcon',
  server: 'ServerIcon',
  virtuale: 'CloudIcon',
  rete: 'GlobeAltIcon',
};

interface DeviceTableProps {
  data: Device[];
  selectedIds: Set<string>;
  onToggleAll: () => void;
  onToggleRow: (id: string) => void;
  onSelectDevice: (d: Device | null) => void;
  selectedDevice: Device | null;
  sortCol: string;
  sortDir: 'asc' | 'desc';
  onSort: (col: string) => void;
  page: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (p: number) => void;
  onDeleteSelected: () => void;
  canWrite: boolean;
}

const columns = [
  { key: 'hostname', label: 'Hostname', sortable: true },
  { key: 'categoria', label: 'Categoria', sortable: true },
  { key: 'stato', label: 'Stato', sortable: true },
  { key: 'ip_address', label: 'Indirizzo IP', sortable: true },
  { key: 'mac_address', label: 'MAC', sortable: false },
  { key: 'reparto', label: 'Reparto', sortable: true },
  { key: 'responsabile', label: 'Responsabile', sortable: false },
  { key: 'modello', label: 'Modello', sortable: false },
  { key: 'ping_ms', label: 'Latenza', sortable: true },
];

export default function DeviceTable({
  data, selectedIds, onToggleAll, onToggleRow, onSelectDevice, selectedDevice,
  sortCol, sortDir, onSort, page, totalPages, totalCount, pageSize, onPageChange,
  onDeleteSelected, canWrite,
}: DeviceTableProps) {
  return (
    <div className="bg-surface border border-border rounded-xl overflow-hidden">
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 bg-sky-500/8 border-b border-sky-500/20">
          <span className="text-base text-sky-400 font-semibold">{selectedIds.size} selezionati</span>
          {canWrite && (
            <button
              onClick={onDeleteSelected}
              className="flex items-center gap-1.5 text-sm bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 rounded-lg px-4 py-2 transition-all font-semibold"
            >
              <Icon name="TrashIcon" size={15} />
              Elimina Selezionati
            </button>
          )}
          <button
            onClick={() => onToggleAll()}
            className="text-sm text-secondary-color hover:text-white transition-colors font-semibold"
          >
            Annulla
          </button>
        </div>
      )}

      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-xs min-w-[900px]">
          <thead>
            <tr className="border-b border-border bg-surface-elevated/50">
              <th className="w-10 px-4 py-3">
                <input
                  type="checkbox"
                  checked={selectedIds.size === data.length && data.length > 0}
                  onChange={onToggleAll}
                  className="w-3.5 h-3.5 rounded border-border bg-surface accent-sky-500"
                />
              </th>
              {columns.map((col) => (
                <th
                  key={`col-${col.key}`}
                  className={`px-4 py-3 text-left font-medium text-muted-color tracking-wide uppercase text-[11px] ${col.sortable ? 'cursor-pointer hover:text-secondary-color select-none' : ''}`}
                  onClick={() => col.sortable && onSort(col.key)}
                >
                  <span className="flex items-center gap-1">
                    {col.label}
                    {col.sortable && sortCol === col.key && (
                      <Icon name={sortDir === 'asc' ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={12} className="text-sky-400" />
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-4 py-16 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <Icon name="ServerIcon" size={36} className="text-muted-color" />
                    <p className="text-base font-semibold text-secondary-color">Nessun dispositivo trovato</p>
                    <p className="text-sm text-muted-color">Modifica i filtri o aggiungi un nuovo dispositivo</p>
                  </div>
                </td>
              </tr>
            ) : (
              data.map((device, idx) => {
                const sc = statusConfig[device.stato] || statusConfig.offline;
                const isSelected = selectedIds.has(device.id);
                const isActive = selectedDevice?.id === device.id;
                return (
                  <tr
                    key={device.id}
                    onClick={() => onSelectDevice(isActive ? null : device)}
                    className={`
                      border-b border-border/50 cursor-pointer transition-colors
                      ${isActive ? 'bg-sky-500/8' : idx % 2 !== 0 ? 'bg-surface-elevated/20' : ''}
                      hover:bg-surface-elevated/40
                    `}
                  >
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onToggleRow(device.id)}
                        className="w-3.5 h-3.5 rounded border-border bg-surface accent-sky-500"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Icon name={categoriaIcons[device.categoria] as any || 'ServerIcon'} size={14} className="text-sky-400 flex-shrink-0" />
                        <span className="font-mono font-semibold text-white">{device.hostname}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="capitalize text-secondary-color">{device.categoria}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${sc.dot}`} />
                        <span className={`font-semibold ${sc.text}`}>{sc.label}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-white">{device.ip_address || '—'}</td>
                    <td className="px-4 py-3 font-mono text-secondary-color">{device.mac_address || '—'}</td>
                    <td className="px-4 py-3 text-secondary-color">{device.reparto || '—'}</td>
                    <td className="px-4 py-3 text-secondary-color">{device.responsabile || '—'}</td>
                    <td className="px-4 py-3 text-secondary-color">{device.modello || '—'}</td>
                    <td className="px-4 py-3">
                      {device.ping_ms !== null ? (
                        <span className={`font-mono font-semibold ${device.ping_ms > 100 ? 'text-amber-400' : 'text-green-400'}`}>
                          {device.ping_ms}ms
                        </span>
                      ) : <span className="text-muted-color">—</span>}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-border">
        <p className="text-sm text-muted-color tabular-nums">
          {totalCount === 0 ? 'Nessun risultato' : `Mostrando ${Math.min((page - 1) * pageSize + 1, totalCount)}–${Math.min(page * pageSize, totalCount)} di ${totalCount} dispositivi`}
        </p>
        <div className="flex items-center gap-1">
          <button
            disabled={page === 1}
            onClick={() => onPageChange(page - 1)}
            className="p-1.5 rounded-lg hover:bg-surface-elevated disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <Icon name="ChevronLeftIcon" size={14} className="text-secondary-color" />
          </button>
          {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
            const p = i + 1;
            return (
              <button
                key={p}
                onClick={() => onPageChange(p)}
                className={`w-7 h-7 rounded-lg text-xs font-semibold transition-colors ${page === p ? 'bg-sky-500 text-white' : 'text-secondary-color hover:bg-surface-elevated'}`}
              >
                {p}
              </button>
            );
          })}
          <button
            disabled={page === totalPages || totalPages === 0}
            onClick={() => onPageChange(page + 1)}
            className="p-1.5 rounded-lg hover:bg-surface-elevated disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <Icon name="ChevronRightIcon" size={14} className="text-secondary-color" />
          </button>
        </div>
      </div>
    </div>
  );
}